import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Users, DollarSign, MessageSquare, TrendingUp, ArrowLeft,
  Bug, Lightbulb, MessageCircle, CheckCircle2, Clock, Eye,
  Download, Search, Crown, Shield, BarChart3
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

export default function AdminDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [userPlanFilter, setUserPlanFilter] = useState("all");
  const [userSearch, setUserSearch] = useState("");
  const [feedbackCategoryFilter, setFeedbackCategoryFilter] = useState("all");
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState("all");

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeTab]);

  const loadData = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      // Always load stats
      const statsRes = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);
      
      if (activeTab === "users") {
        await loadUsers();
      } else if (activeTab === "feedback") {
        await loadFeedback();
      } else if (activeTab === "revenue") {
        const revenueRes = await axios.get(`${API}/admin/revenue`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRevenue(revenueRes.data);
      }
    } catch (error) {
      console.error("Failed to load admin data:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (userPlanFilter !== "all") params.append("plan", userPlanFilter);
      if (userSearch) params.append("search", userSearch);
      
      const res = await axios.get(`${API}/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  };

  const loadFeedback = async () => {
    try {
      const params = new URLSearchParams();
      if (feedbackCategoryFilter !== "all") params.append("category", feedbackCategoryFilter);
      if (feedbackStatusFilter !== "all") params.append("status", feedbackStatusFilter);
      
      const res = await axios.get(`${API}/admin/feedback?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(res.data.feedback);
      setFeedbackStats(res.data.stats);
    } catch (error) {
      console.error("Failed to load feedback:", error);
    }
  };

  const updateFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/feedback/${feedbackId}?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${newStatus}`);
      loadFeedback();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const exportUsersCSV = () => {
    const headers = ["Name", "Email", "Plan", "Country", "Created At"];
    const rows = users.map(u => [
      u.name || "",
      u.email,
      u.tier,
      u.preferences?.default_country || u.preferences?.country || "",
      u.created_at
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "flowguard-users.csv";
    a.click();
  };

  const exportFeedbackCSV = () => {
    const headers = ["Category", "Status", "Message", "User Name", "User Email", "User Plan", "Created At"];
    const rows = feedback.map(f => [
      f.category,
      f.status,
      `"${f.message.replace(/"/g, '""')}"`,
      f.user_name || "",
      f.user_email,
      f.user_plan,
      f.created_at
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "flowguard-feedback.csv";
    a.click();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryBadge = (category) => {
    const config = {
      bug: { icon: Bug, color: "text-red-500", bg: "bg-red-500/10", label: "Bug Report" },
      feature: { icon: Lightbulb, color: "text-blue-500", bg: "bg-blue-500/10", label: "Feature Request" },
      general: { icon: MessageCircle, color: "text-violet-500", bg: "bg-violet-500/10", label: "General" }
    };
    const c = config[category] || config.general;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.color}`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      new: { icon: Clock, color: "text-amber-500", bg: "bg-amber-500/10", label: "New" },
      reviewed: { icon: Eye, color: "text-blue-500", bg: "bg-blue-500/10", label: "Reviewed" },
      resolved: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-500/10", label: "Resolved" }
    };
    const c = config[status] || config.new;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.color}`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    const config = {
      light: { color: "text-slate-400", bg: "bg-slate-500/10" },
      pro: { color: "text-cyan-500", bg: "bg-cyan-500/10" },
      developer: { color: "text-amber-500", bg: "bg-amber-500/10" }
    };
    const c = config[plan] || config.light;
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.color}`}>
        {plan?.charAt(0).toUpperCase() + plan?.slice(1)}
      </span>
    );
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-400">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="admin-dashboard">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-red-500" />
                <h1 className="text-lg font-semibold text-white font-['Manrope']">Admin Dashboard</h1>
              </div>
            </div>
            <ChatToggleButton />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800/50 border border-slate-700 mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-slate-700">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="feedback" className="data-[state=active]:bg-slate-700">
              <MessageSquare className="h-4 w-4 mr-2" />
              Feedback
              {stats?.new_feedback > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {stats.new_feedback}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="revenue" className="data-[state=active]:bg-slate-700">
              <DollarSign className="h-4 w-4 mr-2" />
              Revenue
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="h-5 w-5 text-cyan-500" />
                  <span className="text-slate-400">Total Users</span>
                </div>
                <div className="text-3xl font-bold text-white">{stats?.total_users || 0}</div>
                <div className="text-xs text-slate-500 mt-1">
                  +{stats?.signups_last_7_days || 0} last 7 days
                </div>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Crown className="h-5 w-5 text-amber-500" />
                  <span className="text-slate-400">Subscribers</span>
                </div>
                <div className="text-3xl font-bold text-white">{stats?.total_subscribers || 0}</div>
                <div className="text-xs text-slate-500 mt-1">
                  Pro: {stats?.users_by_plan?.pro || 0} | Dev: {stats?.users_by_plan?.developer || 0}
                </div>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="h-5 w-5 text-emerald-500" />
                  <span className="text-slate-400">MRR</span>
                </div>
                <div className="text-3xl font-bold text-emerald-500">{formatCurrency(stats?.mrr)}</div>
                <div className="text-xs text-slate-500 mt-1">
                  ARR: {formatCurrency(stats?.arr)}
                </div>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <MessageSquare className="h-5 w-5 text-violet-500" />
                  <span className="text-slate-400">Feedback</span>
                </div>
                <div className="text-3xl font-bold text-white">{stats?.total_feedback || 0}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {stats?.new_feedback || 0} new
                </div>
              </div>
            </div>

            {/* Signup Trend Chart */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Signup Trend (Last 30 Days)</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats?.signup_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="_id" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                      labelStyle={{ color: '#f8fafc' }}
                    />
                    <Area type="monotone" dataKey="count" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="glass-card rounded-xl p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h3 className="text-lg font-semibold text-white">User Management</h3>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && loadUsers()}
                      className="pl-9 bg-slate-800 border-slate-700 text-white w-48"
                    />
                  </div>
                  <Select value={userPlanFilter} onValueChange={(v) => { setUserPlanFilter(v); }}>
                    <SelectTrigger className="w-32 bg-slate-800 border-slate-700 text-white">
                      <SelectValue placeholder="Filter by plan" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="all">All Plans</SelectItem>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="developer">Developer</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={loadUsers} variant="outline" size="sm">
                    Apply
                  </Button>
                  <Button onClick={exportUsersCSV} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Plan</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Country</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, idx) => (
                      <tr key={u.id || idx} className="border-b border-slate-800 hover:bg-slate-800/50">
                        <td className="py-3 px-4 text-white">{u.name || "—"}</td>
                        <td className="py-3 px-4 text-slate-300">{u.email}</td>
                        <td className="py-3 px-4">{getPlanBadge(u.tier)}</td>
                        <td className="py-3 px-4 text-slate-400">{u.preferences?.default_country || u.preferences?.country || "—"}</td>
                        <td className="py-3 px-4 text-slate-400 text-sm">{formatDate(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <div className="text-center py-8 text-slate-500">No users found</div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <div className="glass-card rounded-lg p-4 text-center">
                <Bug className="h-5 w-5 text-red-500 mx-auto mb-1" />
                <div className="text-2xl font-bold text-white">{feedbackStats?.bug_reports || 0}</div>
                <div className="text-xs text-slate-500">Bug Reports</div>
              </div>
              <div className="glass-card rounded-lg p-4 text-center">
                <Lightbulb className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                <div className="text-2xl font-bold text-white">{feedbackStats?.feature_requests || 0}</div>
                <div className="text-xs text-slate-500">Feature Requests</div>
              </div>
              <div className="glass-card rounded-lg p-4 text-center">
                <MessageCircle className="h-5 w-5 text-violet-500 mx-auto mb-1" />
                <div className="text-2xl font-bold text-white">{feedbackStats?.general || 0}</div>
                <div className="text-xs text-slate-500">General</div>
              </div>
              <div className="glass-card rounded-lg p-4 text-center">
                <Clock className="h-5 w-5 text-amber-500 mx-auto mb-1" />
                <div className="text-2xl font-bold text-amber-500">{feedbackStats?.new || 0}</div>
                <div className="text-xs text-slate-500">New</div>
              </div>
              <div className="glass-card rounded-lg p-4 text-center">
                <Eye className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                <div className="text-2xl font-bold text-white">{feedbackStats?.reviewed || 0}</div>
                <div className="text-xs text-slate-500">Reviewed</div>
              </div>
              <div className="glass-card rounded-lg p-4 text-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto mb-1" />
                <div className="text-2xl font-bold text-white">{feedbackStats?.resolved || 0}</div>
                <div className="text-xs text-slate-500">Resolved</div>
              </div>
            </div>

            {/* Feedback List */}
            <div className="glass-card rounded-xl p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h3 className="text-lg font-semibold text-white">Feedback Items</h3>
                <div className="flex items-center gap-3">
                  <Select value={feedbackCategoryFilter} onValueChange={(v) => { setFeedbackCategoryFilter(v); }}>
                    <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="bug">Bug Reports</SelectItem>
                      <SelectItem value="feature">Feature Requests</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={feedbackStatusFilter} onValueChange={(v) => { setFeedbackStatusFilter(v); }}>
                    <SelectTrigger className="w-32 bg-slate-800 border-slate-700 text-white">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={loadFeedback} variant="outline" size="sm">
                    Apply
                  </Button>
                  <Button onClick={exportFeedbackCSV} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {feedback.map((f, idx) => (
                  <div key={f.id || idx} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div className="flex flex-wrap items-center gap-2">
                        {getCategoryBadge(f.category)}
                        {getStatusBadge(f.status)}
                        {getPlanBadge(f.user_plan)}
                      </div>
                      <div className="flex items-center gap-2">
                        {f.status !== "reviewed" && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateFeedbackStatus(f.id, "reviewed")}
                            className="h-7 text-xs"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Review
                          </Button>
                        )}
                        {f.status !== "resolved" && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateFeedbackStatus(f.id, "resolved")}
                            className="h-7 text-xs text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10"
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-white mb-3 whitespace-pre-wrap">{f.message}</p>
                    <div className="flex flex-wrap items-center justify-between text-xs text-slate-500">
                      <span>{f.user_name || "Unknown"} ({f.user_email})</span>
                      <span>{formatDate(f.created_at)}</span>
                    </div>
                  </div>
                ))}
                {feedback.length === 0 && (
                  <div className="text-center py-8 text-slate-500">No feedback found</div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="h-5 w-5 text-emerald-500" />
                  <span className="text-slate-400">MRR</span>
                </div>
                <div className="text-3xl font-bold text-emerald-500">{formatCurrency(revenue?.mrr)}</div>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="h-5 w-5 text-cyan-500" />
                  <span className="text-slate-400">ARR</span>
                </div>
                <div className="text-3xl font-bold text-cyan-500">{formatCurrency(revenue?.arr)}</div>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Crown className="h-5 w-5 text-amber-500" />
                  <span className="text-slate-400">Paying Customers</span>
                </div>
                <div className="text-3xl font-bold text-white">{revenue?.total_paying_customers || 0}</div>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="h-5 w-5 text-violet-500" />
                  <span className="text-slate-400">Conversion Rate</span>
                </div>
                <div className="text-3xl font-bold text-white">{revenue?.conversion_rate || 0}%</div>
              </div>
            </div>

            {/* Revenue Breakdown */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6">Revenue Breakdown by Plan</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {revenue?.breakdown && Object.entries(revenue.breakdown).map(([plan, data]) => (
                  <div key={plan} className="p-4 bg-slate-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white font-medium capitalize">{plan}</span>
                      {getPlanBadge(plan)}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Users</span>
                        <span className="text-white">{data.users}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Price/mo</span>
                        <span className="text-white">{formatCurrency(data.price)}</span>
                      </div>
                      <div className="flex justify-between border-t border-slate-700 pt-2">
                        <span className="text-slate-400">Revenue</span>
                        <span className="text-emerald-500 font-semibold">{formatCurrency(data.revenue)}/mo</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
